<template>
  <div class="bg-white shadow-md rounded-lg overflow-hidden transition-all duration-300">
    <!-- 搜索区域头部 -->
    <div class="p-6 border-b border-gray-100">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <template v-for="(filter, index) in filterConfig" :key="index">
          <!-- 输入框类型 -->
          <div v-if="filter.type === 'input'" :class="filter.colSpan || ''">
            <FormInput
              :id="filter.id"
              :label="filter.label"
              :type="filter.inputType || 'text'"
              :placeholder="filter.placeholder"
              :prefix-icon="filter.icon"
              :min="filter.min"
              :max="filter.max"
              v-model="filters[filter.id]"
              :clearable="true"
            />
          </div>

          <!-- 选择框类型 -->
          <div v-else-if="filter.type === 'select'" :class="filter.colSpan || ''">
            <FormSelect
              :id="filter.id"
              :label="filter.label"
              :placeholder="filter.placeholder || `全部${filter.label}`"
              :prefix-icon="filter.icon"
              :options="filter.options"
              v-model="filters[filter.id]"
            />
          </div>

          <!-- 日期范围选择器 -->
          <div v-else-if="filter.type === 'dateRange'" :class="filter.colSpan || ''">
            <label :for="filter.id" class="form-label">{{ filter.label }}</label>
            <DateRangePicker
              :id="filter.id"
              :placeholder="filter.placeholder"
              :prefix-icon="filter.icon"
              :start-field="filter.startField"
              :end-field="filter.endField"
              :model-value="filters"
              @change="(dateRange) => handleDateRangeChange(dateRange, filter.startField, filter.endField)"
              :options="datePickerOptions"
            />
          </div>

          <!-- 数字范围 -->
          <div v-else-if="filter.type === 'numberRange'" class="grid grid-cols-2 gap-3" :class="filter.colSpan || ''">
            <div>
              <FormInput
                :id="`min_${filter.id}`"
                :label="`最小${filter.label}`"
                type="number"
                :prefix="filter.prefix"
                :min="filter.min"
                :max="filter.max"
                :step="filter.step || 1"
                :placeholder="filter.minPlaceholder || '最小'"
                v-model="filters[`min_${filter.id}`]"
              />
            </div>
            <div>
              <FormInput
                :id="`max_${filter.id}`"
                :label="`最大${filter.label}`"
                type="number"
                :prefix="filter.prefix"
                :min="filter.min"
                :max="filter.max"
                :step="filter.step || 1"
                :placeholder="filter.maxPlaceholder || '最大'"
                v-model="filters[`max_${filter.id}`]"
              />
            </div>
          </div>

          <!-- 自定义内容插槽 -->
          <div v-else-if="filter.type === 'slot'" :class="filter.colSpan || ''">
            <slot :name="filter.id"></slot>
          </div>
        </template>
      </div>

      <!-- 自定义内容插槽 -->
      <slot name="additional-filters"></slot>
    </div>

    <!-- 操作按钮 -->
    <div class="bg-gray-50 px-6 py-4 flex justify-end space-x-4">
      <slot name="before-buttons"></slot>
      
      <button 
        @click="resetFilters" 
        type="button"
        class="btn btn-secondary"
      >
        <i class="fas fa-redo mr-1.5"></i> {{ resetButtonText }}
      </button>
      <button 
        @click="search" 
        type="button"
        class="btn btn-primary"
      >
        <i class="fas fa-search mr-1.5"></i> {{ searchButtonText }}
      </button>
      
      <slot name="after-buttons"></slot>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch, nextTick } from 'vue';
import FormInput from './FormInput.vue';
import FormSelect from './FormSelect.vue';
import DateRangePicker from './DateRangePicker.vue';

const props = defineProps({
  // 过滤器配置（每个过滤器的类型、标签等）
  filterConfig: {
    type: Array,
    required: true
  },
  // 初始过滤器值
  initialFilters: {
    type: Object,
    default: () => ({})
  },
  // 按钮文字
  searchButtonText: {
    type: String,
    default: '搜索'
  },
  resetButtonText: {
    type: String,
    default: '重置'
  },
  // 日期选择器选项
  datePickerOptions: {
    type: Object,
    default: () => ({})
  }
});

const emit = defineEmits(['search', 'reset', 'update:filters']);

// 过滤器状态
const filters = reactive({ ...props.initialFilters });

// 监听初始过滤器变化
watch(() => props.initialFilters, (newVal) => {
  Object.keys(newVal).forEach(key => {
    if (newVal[key] !== undefined) {
      filters[key] = newVal[key];
    }
  });
}, { deep: true });

// 生命周期钩子
onMounted(() => {
  // 初始化相关操作
});

// 处理日期范围变更
function handleDateRangeChange(dateRange, startField, endField) {
  if (startField && endField && dateRange) {
    // 直接使用dateRange中的字段名
    filters[startField] = dateRange[startField];
    filters[endField] = dateRange[endField];
    console.log('SearchFilters日期变更:', { startField, endField, values: dateRange });
    
    // 发送更新事件
    emit('update:filters', { ...filters });
  }
}

// 搜索按钮处理程序
function search() {
  emit('search', { ...filters });
}

// 重置筛选条件
function resetFilters() {
  // 重置除了特殊字段外的所有字段
  const excludeFromReset = props.filterConfig
    .filter(f => f.excludeFromReset)
    .map(f => f.id);
  
  // 获取所有日期范围字段的起始和结束字段名
  const dateRangeFields = [];
  props.filterConfig.forEach(filter => {
    if (filter.type === 'dateRange' && filter.startField && filter.endField) {
      dateRangeFields.push(filter.startField);
      dateRangeFields.push(filter.endField);
    }
  });
  
  Object.keys(filters).forEach(key => {
    if (!excludeFromReset.includes(key)) {
      // 对于数字字段，恢复为空字符串或0
      if (typeof props.initialFilters[key] === 'number') {
        filters[key] = 0;
      } else {
        filters[key] = '';
      }
    }
  });
  
  // 确保日期字段被重置
  dateRangeFields.forEach(field => {
    if (!excludeFromReset.includes(field)) {
      filters[field] = '';
    }
  });
  
  console.log('SearchFilters重置后的值:', filters);
  emit('reset', { ...filters });
  emit('update:filters', { ...filters });
}

// 将filters暴露到父组件
defineExpose({
  filters,
  resetFilters
});
</script>

<style scoped>
.form-label {
  @apply block text-sm font-medium text-gray-700 mb-1.5;
}

.form-input {
  @apply block w-full rounded-md border-gray-300 shadow-sm 
  text-gray-700 placeholder-gray-400 sm:text-sm py-2.5
  transition-all duration-200 ease-in-out
  focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500;
}

.btn {
  @apply inline-flex items-center justify-center px-4 py-2.5 border text-sm font-medium rounded-md
  shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 
  transition-all duration-200 ease-in-out;
}

.btn-primary {
  @apply border-transparent text-white bg-gradient-to-r from-blue-500 to-blue-600
  hover:from-blue-600 hover:to-blue-700 hover:shadow-md
  focus:ring-blue-500;
}

.btn-secondary {
  @apply text-gray-700 bg-white border-gray-300 hover:bg-gray-50 hover:shadow
  focus:ring-blue-500;
}
</style> 